import { Caption1, MessageBar, MessageBarBody, makeStyles, tokens } from '@fluentui/react-components'
import { useLastPlanningWrite, usePlanningSaveState } from '../../hooks/usePlanning'
import { writebackRefusedBeforeWriting } from '../../lib/planning'
import { formatRelative } from '../../lib/relativeTime'
import type { PlanningCycle } from '../../types/planning'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, maxWidth: '650px' },
})

export function PlanningStatus({ data }: { data: PlanningCycle }) {
  const styles = useStyles()
  const save = usePlanningSaveState(data.cycle)
  const attempt = useLastPlanningWrite(data.cycle)
  const unmapped = Object.entries(data.github?.unmapped_owners ?? {})
  const written = attempt?.data?.results.filter((r) => r.ok).length ?? 0
  const missed = (attempt?.data?.results.filter((r) => !r.ok).length ?? 0) + (attempt?.data?.skipped.length ?? 0)
  return (
    <div className={styles.root} aria-label="Planning and GitHub status">
      <Caption1>
        {save.pending
          ? `Saving to planning (${save.pending})…`
          : save.failed
            ? 'Some changes were not saved to planning. Review the saved fields and try again.'
            : `Saved to planning${data.updated_by ? ` · ${data.updated_by}` : ''}, ${formatRelative(data.updated_at)}`}
      </Caption1>
      {save.pending > 0 && save.failed > 0 && <Caption1>Some earlier changes were not saved.</Caption1>}
      <Caption1>
        {data.github?.enabled
          ? 'GitHub is separate: review and apply changes in Wrap-up.'
          : data.github?.reason ?? 'GitHub availability is not yet known. Writing is disabled.'}
      </Caption1>
      {attempt?.status === 'pending' && <Caption1>Writing selected changes to GitHub…</Caption1>}
      {attempt?.status === 'success' && (
        <Caption1>Last GitHub attempt this visit: {written} written, {missed} not written. This is not a full-plan sync.</Caption1>
      )}
      {attempt?.status === 'error' && (
        <Caption1>
          {writebackRefusedBeforeWriting(attempt.error)
            ? 'Last GitHub attempt this visit: nothing written.'
            : 'Last GitHub attempt this visit: outcome not confirmed. Check GitHub before retrying.'}
        </Caption1>
      )}
      {unmapped.length > 0 && (
        <MessageBar intent="warning">
          <MessageBarBody>
            Owner changes cannot be assigned on GitHub: {unmapped.map(([name, why]) => `${name} (${why})`).join(', ')}.
            {' '}Use an unambiguous name or ask an admin to set the GitHub handle. This warning does not prevent saving to planning.
          </MessageBarBody>
        </MessageBar>
      )}
    </div>
  )
}
